import { Component } from "react";
import axios from "axios";
import {
    Button,
    Grid,
    ListItemText,
    TextField,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    Divider,
    Typography,
    Box,
    CircularProgress,
} from "@mui/material";
import { ArrowForwardIos, Clear, PhotoSizeSelectActual } from "@mui/icons-material";
import dayjs from "dayjs";
import Layout from "../components/Layout/Layout";
import DateSelect from "../components/DateSelect/index";

const startOfToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const UNASSIGNED_MANAGER = "Án verkstjóra";

const managerName = (line) => line.manager?.name || UNASSIGNED_MANAGER;
// Lines with no manager sort last, whatever the manager names are.
const managerSortKey = (line) => line.manager?.name || "\uffff";
const employeeName = (line) => line.employee?.name || "";

const NOT_STARTED = "#b0b0b0";
const IN_PROGRESS = "#ffc107";
const DONE = "#28a745";

// The admin overview splits a line's progress three ways. The worker's own list keeps the
// original two-state border, where green just means "checked in".
// NOTE: pages/Mowing.js and components/WorklineListItem/WorklineListItem.js still carry a
// copy of that two-state border. Unify them if this split proves out here.
export const lineBorderColor = (line, isAdmin) => {
    if (!isAdmin) return line.checkIn ? DONE : NOT_STARTED;
    if (line.checkOut) return DONE;
    if (line.checkIn) return IN_PROGRESS;
    return NOT_STARTED;
};

// "Nafn 14:00-15:20" once finished, "Nafn 14:00-" while running, bare name before check-in.
export const employeeWorkedLabel = (line) => {
    const name = employeeName(line) || "Óúthlutað";
    if (!line.checkIn) return name;
    return `${name} ${dayjs(line.checkIn).format("HH:mm")}-${line.checkOut ? dayjs(line.checkOut).format("HH:mm") : ""}`;
};

// Group by manager, then employee. Within an employee the server's order (gar_serialnumber asc,
// sorted as text like CRM does) is kept - Array.sort is stable.
export const groupLinesByManager = (lines, searchfilter = "") => {
    const search = searchfilter.trim().toLowerCase();
    const filtered = search
        ? lines.filter((line) =>
              [line.project?.address?.name, line.project?.name, line.name, line.product?.name, employeeName(line), managerName(line), line.serialNumber].some(
                  (value) => value && value.toLowerCase().includes(search)
              )
          )
        : lines;

    const sorted = [...filtered].sort((a, b) => {
        const byManager = managerSortKey(a).localeCompare(managerSortKey(b), "is");
        if (byManager !== 0) return byManager;
        return employeeName(a).localeCompare(employeeName(b), "is");
    });

    return sorted.reduce((groups, line) => {
        const manager = managerName(line);
        const current = groups[groups.length - 1];
        if (current && current.manager === manager) {
            current.lines.push(line);
        } else {
            groups.push({ manager, lines: [line] });
        }
        return groups;
    }, []);
};

export class Gardening extends Component {
    static displayName = Gardening.name;

    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            privileges: this.props.crmPrivileges,
            currentUser: {
                id: props.match.params.hasOwnProperty("id") ? props.match.params.id : props.user.id,
                name: props.match.params.hasOwnProperty("name") ? props.match.params.name : props.user.name,
            },
            isAdminChecked: this.props.location.state != null && this.props.location.state.hasOwnProperty("isAdminChecked") ? props.location.state.isAdminChecked : false,
            projectLines: [],
            searchfilter: this.props.location.state != null && this.props.location.state.hasOwnProperty("searchfilter") ? props.location.state.searchfilter : "",
            selectedDate: this.props.location.state != null && this.props.location.state.selectedDate ? new Date(props.location.state.selectedDate) : startOfToday(),
            adminProjectLines: [],
        };
    }

    componentDidMount() {
        if (this.state.isAdminChecked) {
            this.getAdminGardeningLines(this.state.selectedDate);
        } else {
            this.getGardeningProjectLines(this.state.currentUser.id);
        }
    }

    handleAdminChange = (event) => {
        this.setState({ isAdminChecked: event.target.checked });
        if (event.target.checked) {
            this.getAdminGardeningLines(this.state.selectedDate);
        } else {
            this.getGardeningProjectLines(this.state.currentUser.id);
        }
    };

    handleNewSearchFilter = (event) => {
        this.setState({ searchfilter: event.target.value });
    };

    handleDateChange = (selectedDate) => {
        this.setState({ selectedDate });
        this.getAdminGardeningLines(selectedDate);
    };

    // Keeps the admin view, date and filter when coming back from a line or the photo album.
    saveViewState = () => {
        this.props.history.replace({
            pathname: "/gardening",
            state: {
                isAdminChecked: this.state.isAdminChecked,
                selectedDate: this.state.selectedDate.toISOString(),
                searchfilter: this.state.searchfilter,
            },
        });
    };

    getAdminGardeningLines = (date) => {
        this.setState({ loading: true });

        axios
            .get("/api/gardening/GetAdminGardeningLines", { params: { dueDate: dayjs(date).format("YYYY-MM-DD") } })
            .then((res) => this.setState({ loading: false, adminProjectLines: res.data }))
            .catch(() => this.setState({ loading: false, adminProjectLines: [] }));
    };

    getGardeningProjectLines = (empId) => {
        axios.get("/api/gardening/GetGardeningProjectLines/" + empId).then((res) => {
            let projectLines = res.data;
            this.setState({
                loading: false,
                projectLines,
            });
        });
    };

    hasPrivilege = (privName) => {
        if (this.state.privileges.some((p) => p.name === privName)) {
            return true;
        } else {
            return false;
        }
    };

    goToRoute = (line) => {
        this.saveViewState();
        this.props.history.push("/gardening/" + line.id, { isAdminChecked: this.state.isAdminChecked });
    };

    openPhotos = (id, name, serviceType) => {
        this.saveViewState();
        this.props.history.push("/photos/" + id, { accountLocation: name, currWorkType: serviceType, isAdminChecked: this.state.isAdminChecked });
    };

    renderListItems = (line, isAdmin, primaryText, secondaryText, classes) => {
        const productName = line.product != null && line.product.name != null ? line.product.name : "Óskráð";
        const serialNumber = line.serialNumber != null ? line.serialNumber : "Óskráð";
        const addressName = line.project != null && line.project.address != null && line.project.address.name != null ? line.project.address.name : "Óskráð";
        const serviceType = line.project != null && line.project.serviceType != null ? line.project.serviceType.name : "";
        const estimatedTime = line.quantity != null ? `${line.quantity} hrs` : null;

        return (
            <ListItem sx={{ padding: "0", borderLeft: `6px solid ${lineBorderColor(line, isAdmin)}` }} key={line.id}>
                <ListItemText
                    primary={addressName}
                    primaryTypographyProps={{ style: primaryText }}
                    secondary={
                        <>
                            {productName + " - Serial: " + serialNumber + (estimatedTime ? " - Est: " + estimatedTime : "")}
                            {isAdmin && (
                                <Box component="span" display="block">
                                    {employeeWorkedLabel(line)}
                                </Box>
                            )}
                        </>
                    }
                    secondaryTypographyProps={{ style: secondaryText, component: "div" }}
                    onClick={() => this.goToRoute(line)}
                    style={{ margin: "5px" }}
                />
                <Box>
                    <IconButton onClick={() => this.goToRoute(line)}>
                        <ArrowForwardIos />
                    </IconButton>
                    {isAdmin ? (
                        <IconButton onClick={() => this.openPhotos(line.id, addressName, serviceType)}>
                            <PhotoSizeSelectActual />
                        </IconButton>
                    ) : (
                        ""
                    )}
                </Box>
            </ListItem>
        );
    };

    renderMain = (props) => {
        const classes = props;
        const primaryText = {
            fontSize: "14px",
        };

        const secondaryText = {
            fontSize: "10px",
        };

        const projectLineListToDisplay = this.state.projectLines;

        return (
            <Grid container spacing={3}>
                <Grid container spacing={3} sx={{ justifyContent: "center", alignItems: "center" }}>
                    <Grid item xs={12}>
                        <Box sx={{ maxHeight: "55vh", overflow: "auto", flexGrow: 1 }}>
                            <Box>
                                <List>
                                    {projectLineListToDisplay.map((line) => {
                                        return this.renderListItems(line, false, primaryText, secondaryText, classes);
                                    })}
                                </List>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    renderAdmin = (props) => {
        const { classes } = props;

        const primaryText = {
            fontSize: "14px",
        };

        const secondaryText = {
            fontSize: "10px",
        };

        const groups = groupLinesByManager(this.state.adminProjectLines, this.state.searchfilter);
        const totalCount = groups.reduce((total, group) => total + group.lines.length, 0);
        const isTodaySelected = this.state.selectedDate.getTime() === startOfToday().getTime();

        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <DateSelect date={this.state.selectedDate} setDate={this.handleDateChange} />
                </Grid>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Button
                            onClick={() => this.handleDateChange(startOfToday())}
                            variant={isTodaySelected ? "contained" : "outlined"}
                            style={isTodaySelected ? { color: "#fff", backgroundColor: "#000", borderRadius: 50 } : { borderRadius: 50 }}
                            size="small"
                        >
                            Í dag
                        </Button>
                        <Typography variant="subtitle2" color="textSecondary">
                            {dayjs(this.state.selectedDate).format("DD.MM.YY")} - {totalCount} línur
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="filter"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        label="Filter"
                        variant="outlined"
                        value={this.state.searchfilter}
                        onChange={this.handleNewSearchFilter}
                        InputProps={{
                            endAdornment: this.state.searchfilter ? (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => this.setState({ searchfilter: "" })} aria-label="clear filter">
                                        <Clear />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    {this.state.loading ? (
                        <Box sx={{ textAlign: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        groups.map((group) => (
                            <Box key={group.manager} sx={{ mb: 2 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ pt: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                        {group.manager}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {group.lines.length}
                                    </Typography>
                                </Box>
                                <Divider />
                                <List>{group.lines.map((line) => this.renderListItems(line, true, primaryText, secondaryText, classes))}</List>
                            </Box>
                        ))
                    )}
                    {!this.state.loading && totalCount === 0 && (
                        <Typography variant="body2" color="textSecondary">
                            Engar línur á þessum degi
                        </Typography>
                    )}
                </Grid>
            </Grid>
        );
    };

    render = () => {
        let contents = this.state.isAdminChecked ? (
            this.renderAdmin(this.props)
        ) : this.state.loading ? (
            <Grid container spacing={3} style={{ textAlign: "center" }}>
                <Grid item xs={12}>
                    <CircularProgress />
                </Grid>
            </Grid>
        ) : (
            this.renderMain(this.props)
        );

        const isAdmin = this.hasPrivilege("Garðyrkja super");
        return (
            <Layout
                isAdmin={isAdmin}
                title="Garðyrkja"
                onAdminToggle={isAdmin ? this.handleAdminChange : null}
                color="rgb(225 190 16)"
                backBtnNavigateHome={true}
                isAdminView={this.state.isAdminChecked}
            >
                <Box sx={{ display: "flex", p: "2rem" }}>{contents}</Box>
            </Layout>
        );
    };
}

export default Gardening;
